import { useState, useEffect } from 'react'
import { Wallet, LogOut, AlertCircle } from 'lucide-react'
import { walletService } from '../lib/wallet'
import { useAuthStore } from '../store/authStore'

export function WalletConnect() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const { wallet, connectWallet, disconnectWallet } = useAuthStore()

  useEffect(() => {
    // Listen for account changes
    walletService.onAccountsChanged((accounts: string[]) => {
      if (accounts.length === 0) {
        disconnectWallet()
      } else {
        // Reconnect with new account
        handleConnect()
      }
    })

    // Listen for chain changes
    walletService.onChainChanged(() => {
      window.location.reload()
    })

    return () => {
      walletService.removeListeners()
    }
  }, [])

  const handleConnect = async () => {
    setIsLoading(true)
    setError('')

    try {
      const connected = await walletService.connect()
      if (connected) {
        connectWallet(connected.address, connected.balance, connected.chainId)
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDisconnect = async () => {
    await walletService.disconnect()
    disconnectWallet()
  }

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  if (wallet.isConnected) {
    return (
      <div className="flex items-center space-x-3">
        <div className="hidden md:flex items-center space-x-2 px-3 py-1.5 bg-green-50 rounded-lg">
          <Wallet className="w-4 h-4 text-green-600" />
          <span className="text-sm font-medium text-green-700">
            ${parseFloat(wallet.balance).toFixed(2)}
          </span>
        </div>

        <div className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 rounded-lg">
          <span className="text-sm font-medium text-gray-700">
            {formatAddress(wallet.address!)}
          </span>
          <button
            onClick={handleDisconnect}
            className="p-1 text-gray-400 hover:text-red-600"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center space-x-2">
      {error && (
        <div className="hidden md:flex items-center text-red-600 text-sm">
          <AlertCircle className="w-4 h-4 mr-1" />
          {error}
        </div>
      )}
      <button
        onClick={handleConnect}
        disabled={isLoading}
        className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
      >
        <Wallet className="w-4 h-4" />
        <span>{isLoading ? 'Connecting...' : 'Connect Wallet'}</span>
      </button>
    </div>
  )
}
